import {
  Box,
  Paper,
  Avatar,
  Button,
  Divider,
  Backdrop,
  TextField,
  Typography,
  IconButton,
  Grid2 as Grid,
  InputAdornment,
  Link as MuiLink,
  CircularProgress,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import { Authentication, LocalStorage } from 'utils';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import emailImgSrc from 'assets/images/email-img.png';
import { FormattedMessage, useIntl } from 'react-intl';
import { MouseEvent, useCallback, useState } from 'react';
import logoSrc from 'assets/images/idecon-header-logo.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Copyright, LanguagePicker, ColorModeIconDropdown, SnowSettingsButton } from 'components';

export type LoginFormParams = {
  email: string;
  password: string;
};

const Login = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const onClickLogoHandler = useCallback(() => navigate('login'), [navigate]);

  // useState hooks
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showTwoFactorAuth, setShowTwoFactorAuth] = useState(false);

  // form validations & form state
  const schema = yup.object().shape({
    email: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .email(intl.formatMessage({ id: 'login.invalidEmail', defaultMessage: 'Geçersiz e-posta formatı' }))
      .matches(
        /^[a-zA-Z0-9._%+-]+@(idecon\.com\.tr|ehesapp\.com)$/,
        intl.formatMessage({
          id: 'validations.invalidEmailForIdeconAndEhesapp',
          defaultMessage: 'Idecon ya da Ehesapp e-postası girilmelidir',
        })
      ),
    password: yup
      .string()
      .required(intl.formatMessage({ id: 'general.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<LoginFormParams>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const login = async () => {
    // Numeric control & Empty field control
    if (!code || Number.isNaN(Number(code)) || code.length < 6) {
      enqueueSnackbar(
        <FormattedMessage
          id="login.invalid2FACode"
          defaultMessage="Lütfen rakamlardan oluşan 6 haneli kodu giriniz!"
        />,
        {
          variant: 'error',
        }
      );

      return;
    }

    // If the checks have been passed successfully...
    const postData = {
      email,
      password,
      code,
    };

    try {
      setLoading(true);

      const response = await apiService.signOn(postData);

      if (response.isSuccess) {
        LocalStorage.setItem(STORAGE_KEY_ENUM.LOGGED_USER, response.data.nameSurname);
        Authentication.setToken(response.data.token);

        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      }
    } catch (err: any) {
      if (err?.response?.data?.Message === 'Doğrulama kodu hatalı!') return setCode('');

      handleReset();
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: LoginFormParams) => {
    try {
      setLoading(true);

      const response = await apiService.sendCode(data);

      if (response.isSuccess) {
        setShowTwoFactorAuth(true);
        setEmail(data.email);
        setPassword(data.password);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const goToForgotPasswordPage = useCallback(() => navigate('/forgot-password'), [navigate]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleReset = () => {
    setShowTwoFactorAuth(false);
    setCode('');
    reset();
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <Grid
        size={{ xs: false, sm: 4, md: 7 }}
        sx={{
          backgroundImage:
            'url(https://images.unsplash.com/photo-1586892478025-2b5472316f22?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid size={{ xs: 12, sm: 8, md: 5 }} component={Paper} elevation={2} square>
        <Paper elevation={0}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '10px 20px',
            }}>
            <img style={{ cursor: 'pointer' }} onClick={onClickLogoHandler} width="100px" src={logoSrc} alt="logo" />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 2,
              }}>
              <LanguagePicker />
              <ColorModeIconDropdown />
              <SnowSettingsButton />
            </Box>
          </Box>
        </Paper>
        <Divider />
        <Box
          sx={{
            my: showTwoFactorAuth ? 4 : 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          {showTwoFactorAuth ? (
            <Grid>
              <Grid size={12} display="flex" justifyContent="center">
                <img width="100px" src={emailImgSrc} alt="email-icon" />
              </Grid>
              <Grid size={12} display="flex" justifyContent="center" mt={4}>
                <Typography variant="h4">
                  <FormattedMessage id="login.2FA" defaultMessage="İki Adımlı Doğrulama" />
                </Typography>
              </Grid>
              <Grid size={12} display="flex" justifyContent="center">
                <Typography sx={{ mt: 1, fontSize: 14 }}>
                  <FormattedMessage
                    id="login.openAppMessage"
                    defaultMessage="Lütfen e-posta adresinize gönderilen altı haneli kodu giriniz."
                  />
                </Typography>
              </Grid>
              <Grid size={12}>
                <Box
                  sx={{
                    marginTop: '10%',
                    marginBottom: '10%',
                  }}>
                  <TextField
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    label={<FormattedMessage id="login.otpCode" defaultMessage="OTP Kodu" />}
                    margin="normal"
                    fullWidth
                    autoFocus
                    slotProps={{ htmlInput: { maxLength: 6 } }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') return login();
                    }}
                  />
                </Box>
              </Grid>
              <Grid container size={12} spacing={1}>
                <Grid size={3}>
                  <Button onClick={handleReset} fullWidth disabled={loading} variant="outlined" color="error">
                    <FormattedMessage id="dashboard.cancel" defaultMessage="Vazgeç" />
                  </Button>
                </Grid>
                <Grid size={9}>
                  <Button onClick={login} fullWidth variant="contained">
                    <FormattedMessage id="login.logIn" defaultMessage="Giriş Yap" />
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <>
              <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography sx={{ fontWeight: 'bold' }} component="h1" variant="h5">
                <FormattedMessage id="login.welcome" defaultMessage="Merhaba, Hoşgeldin!" />
              </Typography>
              <Typography component="h5">
                <FormattedMessage
                  id="login.enterInformation"
                  defaultMessage="Devam etmek için lütfen kimlik bilgilerinizi girin"
                />
              </Typography>
              <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={<FormattedMessage id="login.email" defaultMessage="E-Posta" />}
                      margin="normal"
                      fullWidth
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      autoFocus
                      autoComplete="username"
                    />
                  )}
                />
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                sx={{ border: 'none' }}
                                size="small"
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end">
                                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                      label={<FormattedMessage id="login.password" defaultMessage="Parola" />}
                      margin="normal"
                      fullWidth
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      autoComplete="current-password"
                    />
                  )}
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                  <FormattedMessage id="login.logIn" defaultMessage="Giriş Yap" />
                </Button>
                <Grid container spacing={2}>
                  <Grid size={12} display="flex" justifyContent="center">
                    <MuiLink
                      component="button"
                      type="button"
                      onClick={goToForgotPasswordPage}
                      variant="body2"
                      sx={{ alignSelf: 'center' }}>
                      <FormattedMessage id="login.forgotPassword" defaultMessage="Şifreni mi Unuttun?" />
                    </MuiLink>
                  </Grid>
                  <Grid size={12}>
                    <Divider>
                      <FormattedMessage id="login.or" defaultMessage="veya" />
                    </Divider>
                  </Grid>
                  <Grid size={12} display="flex" justifyContent="center">
                    <Link to="/sign-up" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <FormattedMessage id="login.signUp" defaultMessage="Hesabınız yok mu? Kaydolun" />
                    </Link>
                  </Grid>
                </Grid>
                <Copyright />
              </Box>
            </>
          )}
        </Box>
      </Grid>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Grid>
  );
};

export default Login;
